import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfigService } from '@core/services/config.service';
import { NotificationService } from '@core/services/notification.service';
import { PlatformService } from '@modules/core/services/platform.service';
import { TRANSLATION_SCOPE } from '@modules/instructor/i18n/scope';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { ValidatorService } from 'angular-iban';
import { Subject } from 'rxjs';
import { shareReplay, takeUntil } from 'rxjs/operators';
import { Account } from 'src/app/modules/account/models/account';
import { BankAccount } from 'src/app/modules/account/models/bank-account';
import { AccountStoreService } from 'src/app/modules/account/store/account.store.service';

@Component({
  selector: 'bank-account-form',
  templateUrl: './bank-account-form.component.html',
  styleUrls: ['./bank-account-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class BankAccountFormComponent implements OnInit, OnChanges {
  @Input() public isTabReadOnly: boolean = false;

  @Output() public OnBack = new EventEmitter<string>();
  @Output() public OnSubmit = new EventEmitter<string>();
  @Output() public OnComplete = new EventEmitter<string>();
  @Output() public AddBankAccount = new EventEmitter<BankAccount>();

  private stripe: Stripe;

  private account: Account;

  // Internal properties
  accountForm: FormGroup;

  isUpdate = false;
  translationScope = TRANSLATION_SCOPE;
  // Private
  private destroy$: Subject<any>;

  constructor(
    private platform: PlatformService,
    private configService: ConfigService,
    private notificationService: NotificationService,
    private accountStoreService: AccountStoreService
  ) {
    this.destroy$ = new Subject();
    this.createAccountForm(null);
    this.initialStripe();
  }
  ngOnInit(): void {
    this.accountStoreService
      .getBusinessAccount()
      .pipe(shareReplay(1), takeUntil(this.destroy$))
      .subscribe((account) => {
        if (account) {
          this.account = account;
          this.createAccountForm(account);
          if (account.iban) {
            this.isUpdate = true;
            //this.OnComplete.next('bank-account');
          }
        } else {
          this.isUpdate = false;
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.isTabReadOnly.currentValue) {
      this.accountForm.disable();
    } else {
      this.accountForm.enable();
    }
  }

  public Back(): void {
    if (this.platform.isBrowser) {
      window.scroll({
        top: 70,
        left: 100,
        behavior: 'smooth',
      });
    }
    this.OnBack.emit('id-verification');
  }

  public isInvalidateControl(controlName: string, validationType: string): boolean {
    return (
      this.accountForm.get(controlName).hasError(validationType) &&
      (this.accountForm.get(controlName).dirty || this.accountForm.get(controlName).touched)
    );
  }

  private async initialStripe(): Promise<void> {
    this.stripe = await loadStripe(this.configService.config.stripe.public_key);
  }

  private createAccountForm(bankAccount?: Account): void {
    if (bankAccount) {
      this.accountForm.patchValue({
        account_holder_name: bankAccount.name,
        account_number: bankAccount.iban,
      });
      this.accountForm.updateValueAndValidity();
    } else {
      this.accountForm = new FormGroup({
        account_holder_name: new FormControl('', [Validators.required]),
        account_number: new FormControl('', [Validators.required, ValidatorService.validateIban]),
        account_holder_type: new FormControl('individual'),
        currency: new FormControl('nok'),
        country: new FormControl('NO'),
      });
    }
  }

  public async formSubmit(): Promise<void> {
    if (this.accountForm.invalid) {
      if(this.isUpdate) {
        this.OnSubmit.emit('my-courses');
        return
      } else {
        this.accountForm.markAllAsTouched();
        return;
      }
    }

    try {
      const { token } = await this.stripe.createToken('bank_account', this.accountForm.value);
      this.accountStoreService.saveBankIbanNumber(this.account.id, token.id);
      if (this.platform.isBrowser) {
        window.scroll({
          top: 70,
          left: 100,
          behavior: 'smooth',
        });
      }
      this.OnSubmit.emit('my-courses');
    } catch (error) {
      this.notificationService.warning('Please Try again!');
    }
  }

  next(): void {
    if (this.platform.isBrowser) {
      window.scroll({
        top: 70,
        left: 100,
        behavior: 'smooth',
      });
    }
    this.OnSubmit.emit('my-courses');
  }
}
